import {
  Component,
  Inject,
  Input,
  NgZone,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { HealthReportRepo } from '../../../repos/health-report.repo';
import { HttpClient } from '@angular/common/http';
import { FirebaseAuthService } from '../../../noizu/services/firebase-auth.service';
import { ToasterService } from 'angular2-toaster';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AuthService } from '../../../services/auth.service';
import * as echarts from 'echarts';

declare var ResizeObserver: any;

@Component({
  templateUrl: './overview.component.html',
})
export class SystemHealthOverviewComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('echartContainer1', { static: false }) echartContainer1: ElementRef;
  @ViewChild('echartContainer2', { static: false }) echartContainer2: ElementRef;
  @ViewChild('echartContainer3', { static: false }) echartContainer3: ElementRef;

  private chartInstance1: any = null;
  private chartInstance2: any = null;
  private chartInstance3: any = null;
  private resizeObserver: any = null;

  public nodes = [];
  public selected = null;
  public node_reports = {};
  public worker_tallies = {};

  public user_registration_report: any = null;
  public user_registration_report_daily: any = null;
  public user_registration_report_hourly: any = null;
  public system_status_report: any = null;
  public modal_ref: any = null;
  public sensor_daily_report: any = null;
  public sensor_hourly_report: any = null;
  public metric_report: any = {};
  public maintenance_reason = '';
  public show_maintenance = false;
  public showSystemStatus = false;
  public showUserReport = false;

  public data_map: { [key: string]: { name: string; data: any[] } } = {};
  public data_map2: { [key: string]: { name: string; data: any[] } } = {};
  public data_map3: { [key: string]: { name: string; data: any[] } } = {};

  // Kept for backward compatibility
  public graph = { data: [], layout: { width: 1280, height: 480, title: 'Plot' } };
  public graph2 = { data: [], layout: { width: 1280, height: 480, title: 'Plot' } };
  public graph3 = { data: [], layout: { width: 1280, height: 480, title: 'Plot' } };

  public objectKeys(o) {
    return Object.keys(o).sort();
  }

  constructor(
    public reportRepo: HealthReportRepo,
    public modal: BsModalService,
    public authService: AuthService,
    public toaster: ToasterService
  ) {
    this.show_maintenance =
      this.authService.userHasPermission('firmware_admin') ||
      this.authService.userHasPermission('maintenance_admin');
  }

  ngOnInit() {
    this.loadData();
  }

  ngAfterViewInit() {
    this.setupResizeObserver();
    setTimeout(() => {
      this.initCharts();
    }, 150);
  }

  private setupResizeObserver() {
    const RO =
      typeof window !== 'undefined' &&
      ((window as any).ResizeObserver || ResizeObserver);
    if (RO) {
      this.resizeObserver = new RO(() => {
        if (this.chartInstance1) this.chartInstance1.resize();
        if (this.chartInstance2) this.chartInstance2.resize();
        if (this.chartInstance3) this.chartInstance3.resize();
      });
      if (this.echartContainer1 && this.echartContainer1.nativeElement) {
        this.resizeObserver.observe(this.echartContainer1.nativeElement);
      }
      if (this.echartContainer2 && this.echartContainer2.nativeElement) {
        this.resizeObserver.observe(this.echartContainer2.nativeElement);
      }
      if (this.echartContainer3 && this.echartContainer3.nativeElement) {
        this.resizeObserver.observe(this.echartContainer3.nativeElement);
      }
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.chartInstance1) this.chartInstance1.resize();
    if (this.chartInstance2) this.chartInstance2.resize();
    if (this.chartInstance3) this.chartInstance3.resize();
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.chartInstance1) {
      this.chartInstance1.dispose();
      this.chartInstance1 = null;
    }
    if (this.chartInstance2) {
      this.chartInstance2.dispose();
      this.chartInstance2 = null;
    }
    if (this.chartInstance3) {
      this.chartInstance3.dispose();
      this.chartInstance3 = null;
    }
  }

  private initChart(container: ElementRef, currentInstance: any): any {
    if (!container || !container.nativeElement) return null;
    if (container.nativeElement.clientWidth <= 0) return null;
    if (!currentInstance) {
      return echarts.init(container.nativeElement);
    } else {
      currentInstance.resize();
      return currentInstance;
    }
  }

  public initCharts() {
    if (!this.chartInstance1) {
      this.chartInstance1 = this.initChart(this.echartContainer1, this.chartInstance1);
    }
    if (!this.chartInstance2) {
      this.chartInstance2 = this.initChart(this.echartContainer2, this.chartInstance2);
    }
    if (!this.chartInstance3) {
      this.chartInstance3 = this.initChart(this.echartContainer3, this.chartInstance3);
    }
    this.renderChart1();
    this.renderChart2();
    this.renderChart3();
  }

  private createChartOption(
    title: string,
    subtext: string,
    seriesMap: { [key: string]: { name: string; data: any[] } }
  ) {
    const seriesList = Object.values(seriesMap);
    const legendData = seriesList.map((s) => s.name);
    const totalPoints = seriesList.reduce(
      (acc, s) => acc + (s.data ? s.data.length : 0),
      0
    );
    const hasData = totalPoints > 0;
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    const series = seriesList.map((s) => ({
      name: s.name,
      type: 'line',
      smooth: true,
      showSymbol: s.data.length < 50,
      symbolSize: 4,
      data: s.data,
    }));

    return {
      title: {
        text: title,
        subtext: hasData ? subtext : 'No data recorded for this period',
        left: 'center',
        top: 8,
        itemGap: 10,
        textStyle: { fontSize: 16, fontWeight: 'bold', color: '#333' },
        subtextStyle: { fontSize: 12, color: '#888', lineHeight: 20 },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        formatter: (params: any) => {
          if (!params || !params.length) return '';
          const firstTime = params[0].value ? params[0].value[0] : null;
          let dateStr = firstTime ? new Date(firstTime).toLocaleString() : '';
          let res = `<div style="font-weight:bold;margin-bottom:6px;border-bottom:1px solid #eee;padding-bottom:4px;">${dateStr}</div>`;
          for (let p of params) {
            let val = p.value ? p.value[1] : null;
            let displayVal =
              val !== null && val !== undefined
                ? typeof val === 'number'
                  ? Number(val).toLocaleString()
                  : val
                : 'N/A';
            res += `<div style="display:flex;align-items:center;justify-content:space-between;margin:3px 0;gap:12px;">
              <span style="display:flex;align-items:center;">
                <span style="display:inline-block;width:9px;height:9px;border-radius:50%;background-color:${p.color};margin-right:6px;"></span>
                <span>${p.seriesName}</span>
              </span>
              <strong style="margin-left:auto;font-family:monospace;">${displayVal}</strong>
            </div>`;
          }
          return res;
        },
      },
      legend: {
        data: legendData,
        top: 72,
        left: 'center',
        width: '90%',
        type: 'scroll',
        itemGap: 20,
        itemWidth: 14,
        itemHeight: 10,
        textStyle: { fontSize: 12, color: '#555' },
      },
      grid: {
        left: 70,
        right: 40,
        bottom: 75,
        top: 130,
        containLabel: true,
      },
      toolbox: {
        right: 20,
        top: 0,
        feature: {
          dataZoom: {
            xAxisIndex: 0,
            yAxisIndex: 0,
            filterMode: 'filter',
            brushStyle: {
              borderWidth: 1,
              color: 'rgba(135,165,220,0.25)',
              borderColor: 'rgba(100,140,210,0.8)',
            },
          },
          restore: {},
          saveAsImage: {},
        },
      },
      dataZoom: [
        {
          type: 'slider',
          xAxisIndex: 0,
          start: 0,
          end: 100,
          bottom: 12,
          height: 20,
          borderColor: '#d0d5e0',
          fillerColor: 'rgba(100,140,210,0.15)',
          handleStyle: { color: '#6490d2' },
          moveHandleStyle: { color: '#6490d2' },
          textStyle: { color: '#888', fontSize: 11 },
        },
        {
          type: 'inside',
          xAxisIndex: 0,
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
        },
      ],
      xAxis: {
        type: 'time',
        boundaryGap: false,
        min: hasData ? undefined : twentyFourHoursAgo,
        max: hasData ? undefined : now,
      },
      yAxis: {
        type: 'value',
        scale: true,
        splitLine: { lineStyle: { color: '#f0f0f0' } },
        axisLabel: {
          formatter: (val: number) => {
            if (Math.abs(val) >= 1000000) return (val / 1000000).toFixed(1) + 'M';
            if (Math.abs(val) >= 1000) return (val / 1000).toFixed(0) + 'k';
            return val;
          },
        },
      },
      series: series,
    };
  }

  public renderChart1() {
    if (!this.chartInstance1) {
      this.chartInstance1 = this.initChart(this.echartContainer1, this.chartInstance1);
      if (!this.chartInstance1) return;
    }
    const option = this.createChartOption(
      'Backend Workers & Active Sensors',
      'Active workers and nodes across Stage 1, Stage 2, Datastreams, Alerts, and Active Sensors',
      this.data_map
    );
    this.chartInstance1.setOption(option, true);
  }

  public renderChart2() {
    if (!this.chartInstance2) {
      this.chartInstance2 = this.initChart(this.echartContainer2, this.chartInstance2);
      if (!this.chartInstance2) return;
    }
    const option = this.createChartOption(
      'Gateway Traffic & System Errors',
      'Binary device reports processed/dropped and system error metrics',
      this.data_map2
    );
    this.chartInstance2.setOption(option, true);
  }

  public renderChart3() {
    if (!this.chartInstance3) {
      this.chartInstance3 = this.initChart(this.echartContainer3, this.chartInstance3);
      if (!this.chartInstance3) return;
    }
    const option = this.createChartOption(
      'User Registrations & Station Connectivity',
      'User signups, display console users, and unconnected stations (daily & hourly)',
      this.data_map3
    );
    this.chartInstance3.setOption(option, true);
  }

  public refresh_graph() {
    this.renderChart1();
  }

  public refresh_graph2() {
    this.renderChart2();
  }

  public refresh_graph3() {
    this.renderChart3();
  }

  private loadData() {
    // 1. Stage 2 Workers
    this.reportRepo.getWorkerReport('stage2').then((report: any) => {
      if (report && report['results']) {
        let active_workers = [];
        let active_nodes = [];
        for (let i = 0; i < report.results.length; i++) {
          let e = report.results[i];
          let t = e.t ? e.t * 1000 : null;
          if (t) {
            active_workers.push([t, e.aw]);
            active_nodes.push([t, e.n]);
          }
        }
        active_workers.sort((a, b) => a[0] - b[0]);
        active_nodes.sort((a, b) => a[0] - b[0]);
        this.data_map['s2-workers'] = { name: 's2 workers', data: active_workers };
        this.data_map['s2-nodes'] = { name: 's2 nodes', data: active_nodes };
        this.renderChart1();
      }
    });

    // 2. Stage 1 Workers
    this.reportRepo.getWorkerReport('stage1').then((report: any) => {
      if (report && report['results']) {
        let active_workers = [];
        let active_nodes = [];
        for (let i = 0; i < report.results.length; i++) {
          let e = report.results[i];
          let t = e.t ? e.t * 1000 : null;
          if (t) {
            active_workers.push([t, e.aw]);
            active_nodes.push([t, e.n]);
          }
        }
        active_workers.sort((a, b) => a[0] - b[0]);
        active_nodes.sort((a, b) => a[0] - b[0]);
        this.data_map['s1-workers'] = { name: 's1 workers', data: active_workers };
        this.data_map['s1-nodes'] = { name: 's1 nodes', data: active_nodes };
        this.renderChart1();
      }
    });

    // 3. Datastreams
    this.reportRepo.getWorkerReport('datastreams').then((report: any) => {
      if (report && report['results']) {
        let active_workers = [];
        let active_nodes = [];
        for (let i = 0; i < report.results.length; i++) {
          let e = report.results[i];
          let t = e.t ? e.t * 1000 : null;
          if (t) {
            active_workers.push([t, e.aw]);
            active_nodes.push([t, e.n]);
          }
        }
        active_workers.sort((a, b) => a[0] - b[0]);
        active_nodes.sort((a, b) => a[0] - b[0]);
        this.data_map['ds-workers'] = { name: 'ds workers', data: active_workers };
        this.data_map['ds-nodes'] = { name: 'ds nodes', data: active_nodes };
        this.renderChart1();
      }
    });

    // 4. Alerts
    this.reportRepo.getWorkerReport('alerts').then((report: any) => {
      if (report && report['results']) {
        let active_workers = [];
        let active_nodes = [];
        for (let i = 0; i < report.results.length; i++) {
          let e = report.results[i];
          let t = e.t ? e.t * 1000 : null;
          if (t) {
            active_workers.push([t, e.aw]);
            active_nodes.push([t, e.n]);
          }
        }
        active_workers.sort((a, b) => a[0] - b[0]);
        active_nodes.sort((a, b) => a[0] - b[0]);
        this.data_map['alert-workers'] = { name: 'alert workers', data: active_workers };
        this.data_map['alert-nodes'] = { name: 'alert nodes', data: active_nodes };
        this.renderChart1();
      }
    });

    // 5. Active Sensors
    this.reportRepo.getDailyMetrics('active_sensors').then((report: any) => {
      if (report && report['results']) {
        let count = [];
        for (let i = 0; i < report.results.length; i++) {
          let e = report.results[i];
          let t = e.t ? e.t * 1000 : null;
          if (t) count.push([t, e.c]);
        }
        count.sort((a, b) => a[0] - b[0]);
        this.data_map['daily.sensors'] = { name: 'daily sensors', data: count };
        this.renderChart1();
      }
    });

    // 6. User Registration & Connection Plots
    this.reportRepo.getUserRegistrationReport().then((report) => (this.user_registration_report = report));

    this.reportRepo.getUserRegistrationPlot('day').then((report: any) => {
      if (report && report['results']) {
        let users = [];
        let has_display = [];
        let not_connected = [];
        for (let i = 0; i < report.results.length; i++) {
          let e = report.results[i];
          let t = e.t ? e.t * 1000 : null;
          if (t) {
            users.push([t, e.u]);
            has_display.push([t, e.d]);
            not_connected.push([t, e.n]);
          }
        }
        users.sort((a, b) => a[0] - b[0]);
        has_display.sort((a, b) => a[0] - b[0]);
        not_connected.sort((a, b) => a[0] - b[0]);
        this.data_map3['day.users'] = { name: 'day users', data: users };
        this.data_map3['day.users.display'] = { name: 'day users.display', data: has_display };
        this.data_map3['day.users.nc'] = { name: 'day users.not_connected', data: not_connected };
        this.renderChart3();
      }
    });

    this.reportRepo.getUserRegistrationPlot('hour').then((report: any) => {
      if (report && report['results']) {
        let users = [];
        let has_display = [];
        let not_connected = [];
        for (let i = 0; i < report.results.length; i++) {
          let e = report.results[i];
          let t = e.t ? e.t * 1000 : null;
          if (t) {
            users.push([t, e.u]);
            has_display.push([t, e.d]);
            not_connected.push([t, e.n]);
          }
        }
        users.sort((a, b) => a[0] - b[0]);
        has_display.sort((a, b) => a[0] - b[0]);
        not_connected.sort((a, b) => a[0] - b[0]);
        this.data_map3['hour.users'] = { name: 'hour users', data: users };
        this.data_map3['hour.users.display'] = { name: 'hour users.display', data: has_display };
        this.data_map3['hour.users.nc'] = { name: 'hour users.not_connected', data: not_connected };
        this.renderChart3();
      }
    });

    // 7. Gateway Worker & Error Metric Plots
    const metricConfigs = [
      { metric: 'gateway.worker.report_binary', key: 'report_binary', name: 'report_binary' },
      { metric: 'gateway.worker.report_binary.drop', key: 'report_binary.drop', name: 'report_binary.drop' },
      { metric: 'request.json.error', key: 'json.error', name: 'json error' },
      { metric: 'request.serial.error', key: 'serial.error', name: 'serial error' },
      { metric: 'request.timeout.error', key: 'timeout.error', name: 'timeout error' },
      { metric: 'request.uncaught.error', key: 'uncaught.error', name: 'uncaught error' },
    ];

    for (let cfg of metricConfigs) {
      this.reportRepo.getMetricPlot(cfg.metric).then((report: any) => {
        if (report && report['results']) {
          let count = [];
          for (let i = 0; i < report.results.length; i++) {
            let e = report.results[i];
            let t = e.t ? e.t * 1000 : null;
            if (t) count.push([t, e.c]);
          }
          count.sort((a, b) => a[0] - b[0]);
          this.data_map2[cfg.key] = { name: cfg.name, data: count };
          this.renderChart2();
        }
      });
    }

    this.reportRepo.getSystemStatus().then((report) => (this.system_status_report = report));
  }

  public clear_maintenance(event) {
    event.preventDefault();
    this.reportRepo.clearMaintenance().then(() => {
      this.toaster.pop('info', 'Maintenance Mode', 'Cleared.');
    });
  }

  public schedule_maintenance(event, modalElm) {
    event.preventDefault();
    this.modal_ref = this.modal.show(modalElm);
  }

  public confirm_maintenance(event, reason) {
    event.preventDefault();
    this.reportRepo.scheduleMaintenance(reason).then(() => {
      this.toaster.pop('info', 'Maintenance Mode', 'Scheduled.');
      this.modal_ref.hide();
    });
  }
}
